import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';
import { NewsSession, NewsSessionEvents } from './session';
import { NewsStore } from './store';
import { Story } from './story';
import { NewsBZQLQuery } from './entities';
import { SafePromise } from '@benzinga/safe-await';

interface NewsFeedOpen {
  type: 'news:open';
}

interface NewsFeedClose {
  type: 'news:close';
}

interface ReconnectEvent {
  type: `news:${'reconnected' | 'disconnected'}`;
}

interface NewsFeedStories {
  stories: Story[];
  type: 'news:new_stories' | 'news:reconnect_stories';
}

interface NewsFeedFutureHistoricStories {
  hasMoreHistory: boolean;
  stories: Story[];
  type: 'news:future_historic_stories';
}
interface NewsFeedPastHistoricStories {
  hasMoreHistory: boolean;
  stories: Story[];
  type: 'news:past_historic_stories';
}

export type NewsFeedEvents =
  | NewsFeedOpen
  | NewsFeedClose
  | ReconnectEvent
  | NewsFeedStories
  | NewsFeedFutureHistoricStories
  | NewsFeedPastHistoricStories;

interface SubscriptionExtension {
  getHistoric: (numStoriesRequested?: number, direction?: 'past' | 'future') => SafePromise<Story[]>;
  getQuery: () => NewsBZQLQuery;
  startFeed: () => void;
}

export class NewsFeed extends ExtendedSubscribable<NewsFeedEvents, SubscriptionExtension> {
  static count = 0;
  private session: NewsSession;
  private store: NewsStore;
  private sessionSubscription?: Subscription<NewsSession>;
  private state: 'running' | 'waiting_for_subs' | 'stopped';
  private hasSubs: boolean;
  private hasMoreFutureHistory = true;
  private hasMorePastHistory = true;

  constructor(session: NewsSession, store: NewsStore) {
    super();
    this.session = session;
    this.store = store;
    this.state = 'stopped';
    this.hasSubs = false;
  }

  public startFeed = (): void => {
    if (this.hasSubs) {
      this.sessionSubscription = this.session.subscribe(this.socketCallback);
      this.store.addActiveNewsFeed(this.session.getFeedId(), this);
      this.state = 'running';
    } else {
      this.state = 'waiting_for_subs';
    }
  };

  public getHistoric = async (
    numStoriesRequested = 100,
    direction: 'past' | 'future' = 'past',
  ): SafePromise<Story[]> => {
    if (direction === 'past') {
      const stories = await this.session.getPastHistoric(numStoriesRequested);
      if (stories.err) {
        return { err: stories.err };
      } else {
        this.hasMorePastHistory = stories.ok.length === numStoriesRequested;
        const processedStories = [...new Set(await this.store.processMultipleHistoricStories(stories.ok))];
        this.dispatch({
          hasMoreHistory: this.hasMorePastHistory,
          stories: processedStories,
          type: 'news:past_historic_stories',
        });
        return { ok: processedStories };
      }
    } else {
      const stories = await this.session.getFutureHistoric(numStoriesRequested);
      if (stories.err) {
        return { err: stories.err };
      } else {
        this.hasMoreFutureHistory = stories.ok.length === numStoriesRequested;
        const processedStories = [...new Set(await this.store.processMultipleHistoricStories(stories.ok))];
        this.dispatch({
          hasMoreHistory: this.hasMoreFutureHistory,
          stories: processedStories,
          type: 'news:future_historic_stories',
        });
        return { ok: processedStories };
      }
    }
  };

  public getHasMorePastHistory = () => this.hasMorePastHistory;

  public getHasMoreFutureHistory = () => this.hasMoreFutureHistory;

  public getQuery = (): NewsBZQLQuery => this.session.getQuery();

  protected onSubscribe(): SubscriptionExtension {
    return {
      getHistoric: this.getHistoric,
      getQuery: this.getQuery,
      startFeed: this.startFeed,
    };
  }

  protected onFirstSubscription(): void {
    if (this.state === 'waiting_for_subs') {
      this.sessionSubscription = this.session.subscribe(this.socketCallback);
      this.store.addActiveNewsFeed(this.session.getFeedId(), this);
    }
    this.hasSubs = true;
  }

  protected onZeroSubscriptions(): void {
    this.hasSubs = false;
    this.state = 'stopped';
    this.sessionSubscription?.unsubscribe();
    this.sessionSubscription = undefined;
    this.store.removeActiveNewsFeed(this.session.getFeedId());
  }

  private socketCallback = async (event: NewsSessionEvents): Promise<void> => {
    switch (event.type) {
      case 'news:new_stories':
      case 'news:reconnect_stories': {
        const stories = [...new Set(await this.store.processMultipleNewStories(event.stories))];
        this.dispatch({ stories, type: event.type });
        break;
      }
      case 'news:future_historic_stories':
      case 'news:past_historic_stories':
        break; // we are doing this in the getHistoric function so we don't have process twice
      default:
        this.dispatch(event);
        break;
    }
  };
}
